import { useMemo } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'

import { ProductLotStockOutForm } from './new'
import type { CreateOutNoteRequest } from '@/lib/api/product-lot-api'
import type { ProductLotOption } from '@/components/product-lots/ProductLotItemsField'
import { productNoteItemToOption } from '@/components/product-lots/product-lot-option'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import { useProductLotOutNoteQuery } from '@/hooks/use-product-lot-query'
import { useUpdateProductLotOutNote } from '@/hooks/use-product-lot-mutation'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/sales-warehouse/stock-out-notes/$id_/edit',
)({
    component: Page,
})

function Page() {
    const { id } = Route.useParams()
    const navigate = useNavigate()
    const { user } = useAuth()
    const { data, isLoading } = useProductLotOutNoteQuery(id)
    const note = data?.data
    const update = useUpdateProductLotOutNote()

    const seedLotOptions = useMemo<ProductLotOption[]>(
        () => (note?.items ?? []).map(productNoteItemToOption),
        [note],
    )

    const defaultValues = useMemo(() => {
        if (!note) return null
        return {
            reason: note.reason,
            notes: note.notes ?? '',
            items: (note.items ?? []).map((it) => ({
                kind: 'existing' as const,
                product_lot_id: it.product_lot_id,
                quantity: it.quantity,
            })),
        }
    }, [note])

    if (isLoading) return <div className="p-6">{m.sl_detail_loading()}</div>
    if (!note || !defaultValues)
        return <div className="p-6">{m.sl_detail_note_not_found()}</div>

    const isCreator = !!user?.id && note.created_by_id === user.id
    const editable =
        note.status === 'pending' && isCreator && !note.stocktake_session_id

    if (!editable) {
        return (
            <div className="space-y-4 p-6">
                <Button asChild variant="ghost" className="-ml-2">
                    <Link
                        to="/sales-warehouse/stock-out-notes/$id"
                        params={{ id }}
                    >
                        <ArrowLeft className="size-4 mr-1" />
                        {m.sl_lot_back()}
                    </Link>
                </Button>
                <p className="text-muted-foreground">
                    {m.pl_pnk_edit_not_allowed()}
                </p>
            </div>
        )
    }

    const backToDetail = () =>
        navigate({
            to: '/sales-warehouse/stock-out-notes/$id',
            params: { id },
            replace: true,
        })

    const handleSubmit = async (req: CreateOutNoteRequest) => {
        await update.mutateAsync({ id, req })
        void backToDetail()
    }

    return (
        <ProductLotStockOutForm
            mode="edit"
            title={m.pll_pxk_edit_title()}
            submitLabel={m.pl_pnk_edit_submit()}
            isPending={update.isPending}
            defaultValues={defaultValues}
            seedLotOptions={seedLotOptions}
            onSubmit={handleSubmit}
            onCancel={() => void backToDetail()}
        />
    )
}
